import * as React from 'react'
import {
  ChangeEvent,
  CompositionEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFormikContext } from 'formik'
import debounce from '@mui/material/utils/debounce'
import { truncateToMax } from '@user/components/Formik/DebouncedTextField'
import type {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from './typings'
import { TextField } from './atoms'
import { useGetError } from './useGetError'
import { useHideOptional } from './useHideOptional'
import { CUSTOM_FIELD_TEXT_MAX_LENGTH } from '../constants'
import { Theme } from '@mui/material/styles'

export function EditableCustomText(props: EditableCustomFieldProps) {
  const {
    whiteBackground = false,
    avoidValidateOnBlur = false,
    margin,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    textFieldProps,
  } = props

  const {
    setFieldValue,
    initialValues: {
      customFields: { text },
    },
    handleBlur,
  } = useFormikContext<ICustomFieldValueFormModel>()

  const initialFieldValue = text[fieldId]?.value ?? ''

  const [value, setValue] = useState(initialFieldValue)

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(truncateToMax(e.target.value, CUSTOM_FIELD_TEXT_MAX_LENGTH))
    },
    [],
  )

  const onCompositionEnd = useCallback(
    (e: CompositionEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement
      const truncated = truncateToMax(
        target.value,
        CUSTOM_FIELD_TEXT_MAX_LENGTH,
      )
      if (truncated !== target.value) {
        target.value = truncated
        setValue(truncated)
      }
    },
    [],
  )

  const update = useMemo(
    () =>
      debounce(
        (v: string | null) =>
          setFieldValue(`customFields.text[${fieldId}].value`, v),
        200,
      ),
    [fieldId, setFieldValue],
  )

  useEffect(() => {
    update.clear()
    update(value)
  }, [update, value])

  useEffect(() => {
    setValue(initialFieldValue)
  }, [initialFieldValue])

  const { hasError: fieldError, errorText } = useGetError(props)

  const helperText = fieldError
    ? errorText
    : `${value?.length ?? 0}/${CUSTOM_FIELD_TEXT_MAX_LENGTH}`

  const showField = useHideOptional(props)

  if (!showField) return null
  return (
    <TextField
      fullWidth
      label={name}
      value={value}
      margin={margin}
      onChange={onChange}
      error={fieldError}
      helperText={helperText}
      required={required}
      name={`customFields.text[${fieldId}]`}
      onBlur={!avoidValidateOnBlur ? handleBlur : undefined}
      {...textFieldProps}
      inputProps={{
        ...textFieldProps?.inputProps,
        maxLength: CUSTOM_FIELD_TEXT_MAX_LENGTH,
        onCompositionEnd: (
          event: CompositionEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => {
          // Compose with any consumer-supplied handler rather than replacing
          // it: `textFieldProps` is this component's extension point and seven
          // call sites funnel through here. Mirrors DebouncedTextField.
          textFieldProps?.inputProps?.onCompositionEnd?.(
            event as CompositionEvent<HTMLInputElement>,
          )
          onCompositionEnd(event)
        },
        sx: {
          backgroundColor: whiteBackground
            ? (theme: Theme) => theme.palette.common.white
            : 'transparent',
        },
      }}
      data-test-id={`custom-text-field-${fieldId}`}
    />
  )
}
